/** Plain-language explanation of an alert, shown inside the alert detail modal. */
import * as React from "react";
import { LifeBuoy, Sparkles } from "lucide-react";
import { Button } from "../../../../ui/components/Button";
import { AuthTransitionScreen } from "../../../../ui/components/AuthTransitionScreen";
import { useT } from "../../../lib/i18n";
import * as insightsClient from "../../../lib/api/insights";
import type { InsightSupportFields, InsightSupportRequest, InsightsApi, ProductInsight } from "../../../lib/api/insights";
import {
  InsightDetailsView,
  InsightFooter,
  InsightSources,
  LION_SUPPORT_PHONE,
  SupportConfirmation,
  detailsErrorMessage,
} from "./InsightCard";
import { SupportRequestModal } from "./SupportRequestModal";
import * as s from "./AlertInsightPanel.css";

type PanelState =
  | { kind: "loading" }
  | { kind: "ready"; insight: ProductInsight; detailsError: string | null }
  | { kind: "none" }
  | { kind: "error" };

export function AlertInsightPanel({
  productId,
  alertCode,
  alertName,
  token,
  showUsage = false,
  api = insightsClient,
}: {
  productId: string;
  alertCode: string;
  /** Alert display name, shown in the support modal. */
  alertName?: string;
  token: string | null;
  showUsage?: boolean;
  api?: InsightsApi;
}) {
  const t = useT();
  const [state, setState] = React.useState<PanelState>({ kind: "loading" });
  const [request, setRequest] = React.useState<InsightSupportRequest | null>(null);
  const [requesting, setRequesting] = React.useState(false);
  const [supportError, setSupportError] = React.useState<string | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const tRef = React.useRef(t);
  tRef.current = t;

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setState({ kind: "loading" });
    setRequest(null);
    (async () => {
      try {
        const found = await api.fetchAlertInsight(token, productId, alertCode);
        if (cancelled) return;
        if (!found) return setState({ kind: "none" });
        if (found.details) return setState({ kind: "ready", insight: found, detailsError: null });
        // Opening an alert is an explicit request, so generate the details if needed.
        try {
          const withDetails = await api.fetchInsightDetails(token, productId, found.id);
          if (!cancelled) setState({ kind: "ready", insight: withDetails, detailsError: null });
        } catch (err) {
          if (!cancelled) setState({ kind: "ready", insight: found, detailsError: detailsErrorMessage(err, tRef.current, showUsage) });
        }
      } catch {
        if (!cancelled) setState({ kind: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, productId, alertCode, showUsage]);

  const requestAlertSupport = async (fields: InsightSupportFields) => {
    if (!token || requesting) return;
    setRequesting(true);
    setSupportError(null);
    try {
      setRequest(await api.requestAlertSupport(token, productId, alertCode, fields));
      setModalOpen(false);
    } catch {
      setSupportError(t("pro.products.insights.support.failed", { phone: LION_SUPPORT_PHONE }));
    } finally {
      setRequesting(false);
    }
  };

  if (state.kind === "loading") {
    return (
      <div className={s.panel}>
        <AuthTransitionScreen variant="compact" message={t("pro.products.insights.analyzingAlert")} />
      </div>
    );
  }

  if (state.kind === "none" || state.kind === "error") {
    return (
      <div className={s.panel}>
        <div className={s.panelHeader}>
          <Sparkles size={14} aria-hidden /> {t("pro.products.insights.panelTitle")}
        </div>
        <p className={s.panelText}>
          {state.kind === "none" ? t("pro.products.insights.noGuidanceForAlert") : t("pro.products.insights.loadFailed")}
        </p>
        {request ? (
          <SupportConfirmation request={request} />
        ) : (
          <>
            <Button
              type="button"
              size="sm"
              color="neutral"
              variant="outline"
              iconLeft={<LifeBuoy size={14} />}
              onClick={() => {
                setSupportError(null);
                setModalOpen(true);
              }}
            >
              {t("pro.products.insights.support.contact")}
            </Button>
            <SupportRequestModal
              open={modalOpen}
              subject={alertName ?? alertCode}
              alertCode={alertName ? alertCode : null}
              loading={requesting}
              error={supportError}
              onCancel={() => setModalOpen(false)}
              onSubmit={(fields) => void requestAlertSupport(fields)}
            />
          </>
        )}
      </div>
    );
  }

  const { insight, detailsError } = state;
  return (
    <div className={s.panel}>
      <div className={s.panelHeader}>
        <Sparkles size={14} aria-hidden /> {t("pro.products.insights.whatThisMeans")}
      </div>
      <h3 className={s.panelTitle}>{insight.title}</h3>
      <p className={s.panelText}>{insight.summary}</p>
      {insight.details ? (
        <>
          <InsightDetailsView details={insight.details} />
          <InsightSources details={insight.details} />
        </>
      ) : detailsError ? (
        <p className={s.panelText}>{detailsError}</p>
      ) : null}
      <InsightFooter
        insight={insight}
        showUsage={showUsage}
        onFeedback={
          insight.details
            ? (target, feedback) => {
                if (!token) return;
                setState({ kind: "ready", insight: { ...target, feedback }, detailsError });
                api.submitInsightFeedback(token, productId, target.id, feedback).catch(() => {});
              }
            : undefined
        }
        onRequestSupport={async (target, fields) => {
          if (!token) throw new Error("Not signed in");
          return api.requestInsightSupport(token, productId, target.id, fields);
        }}
      />
    </div>
  );
}
